export const API_BASE =
  (import.meta.env.VITE_SERVER_URL as string) || "http://localhost:3001";

export interface UserProfile {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
  emailVerified: boolean;
  hasPassword: boolean;
  role: string;
}

interface AuthResponse {
  user: UserProfile;
}

interface MessageResponse {
  message: string;
}

class AuthApiError extends Error {
  constructor(
    message: string,
    public status: number
  ) {
    super(message);
    this.name = "AuthApiError";
  }
}

async function authFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const res = await fetch(`${API_BASE}/api${path}`, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...options.headers
    }
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: "Request failed" }));
    throw new AuthApiError(
      body.message || `Request failed with status ${res.status}`,
      res.status
    );
  }

  if (res.status === 204) return {} as T;
  return res.json();
}

/** Attempt token refresh, then retry the original request once. */
async function authFetchWithRefresh<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  try {
    return await authFetch<T>(path, options);
  } catch (err) {
    if (err instanceof AuthApiError && err.status === 401) {
      // Try refreshing the access token
      const refreshed = await refreshTokens().catch(() => false);
      if (refreshed) {
        return authFetch<T>(path, options);
      }
    }
    throw err;
  }
}

// --- Auth endpoints ---

export async function signup(
  email: string,
  password: string,
  displayName: string
): Promise<UserProfile> {
  const data = await authFetch<AuthResponse>("/auth/signup", {
    method: "POST",
    body: JSON.stringify({ email, password, displayName })
  });
  return data.user;
}

export async function login(
  email: string,
  password: string
): Promise<UserProfile> {
  const data = await authFetch<AuthResponse>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password })
  });
  return data.user;
}

export async function logout(): Promise<void> {
  await authFetch<MessageResponse>("/auth/logout", { method: "POST" });
}

export async function refreshTokens(): Promise<boolean> {
  try {
    await authFetch<MessageResponse>("/auth/refresh", { method: "POST" });
    return true;
  } catch {
    return false;
  }
}

export async function getMe(): Promise<UserProfile> {
  return authFetchWithRefresh<UserProfile>("/auth/me");
}

export async function verifyEmail(token: string): Promise<void> {
  await authFetch<MessageResponse>("/auth/verify-email", {
    method: "POST",
    body: JSON.stringify({ token })
  });
}

export async function resendVerification(): Promise<void> {
  await authFetchWithRefresh<MessageResponse>("/auth/resend-verification", {
    method: "POST"
  });
}

export async function forgotPassword(email: string): Promise<void> {
  await authFetch<MessageResponse>("/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email })
  });
}

export async function resetPassword(
  token: string,
  newPassword: string
): Promise<void> {
  await authFetch<MessageResponse>("/auth/reset-password", {
    method: "POST",
    body: JSON.stringify({ token, newPassword })
  });
}

// --- User endpoints ---

export async function updateProfile(displayName: string): Promise<UserProfile> {
  return authFetchWithRefresh<UserProfile>("/users/profile", {
    method: "PUT",
    body: JSON.stringify({ displayName })
  });
}

export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<void> {
  await authFetchWithRefresh<MessageResponse>("/users/password", {
    method: "PUT",
    body: JSON.stringify({ currentPassword, newPassword })
  });
}

export async function setPassword(newPassword: string): Promise<void> {
  await authFetchWithRefresh<MessageResponse>("/users/set-password", {
    method: "PUT",
    body: JSON.stringify({ newPassword })
  });
}

export interface OAuthAccount {
  provider: string;
  createdAt: string;
}

export async function getOAuthAccounts(): Promise<OAuthAccount[]> {
  return authFetchWithRefresh<OAuthAccount[]>("/users/oauth-accounts");
}

export async function unlinkOAuthAccount(provider: string): Promise<void> {
  await authFetchWithRefresh<MessageResponse>(
    `/users/oauth-accounts/${encodeURIComponent(provider)}`,
    { method: "DELETE" }
  );
}

export async function deleteAccount(): Promise<void> {
  await authFetchWithRefresh<MessageResponse>("/users/account", {
    method: "DELETE"
  });
}

export { AuthApiError, authFetchWithRefresh };
